import {
  FlexiblePaymentFrequency,
  PaymentFrequencyEnum,
  PaymentPlanType,
  SnakeFlexiblePaymentFrequency,
} from './types';

const FREQUENCY_TO_SNAKE_MAP: Record<
  FlexiblePaymentFrequency,
  SnakeFlexiblePaymentFrequency
> = {
  weekly: 'weekly',
  monthly: 'monthly',
  biWeekly: 'bi_weekly',
  semiMonthly: 'semi_monthly',
  fullPay: 'full_pay',
};

const SNAKE_TO_FREQUENCY_MAP: Record<
  SnakeFlexiblePaymentFrequency,
  FlexiblePaymentFrequency
> = {
  weekly: 'weekly',
  monthly: 'monthly',
  bi_weekly: 'biWeekly',
  semi_monthly: 'semiMonthly',
  full_pay: 'fullPay',
};

export const toSnakeFlexiblePaymentFrequency = (
  frequency: FlexiblePaymentFrequency
): SnakeFlexiblePaymentFrequency => {
  return FREQUENCY_TO_SNAKE_MAP[frequency];
};

export const fromSnakeFlexiblePaymentFrequency = (
  value?: string | null
): FlexiblePaymentFrequency | null => {
  if (!value) {
    return null;
  }

  return SNAKE_TO_FREQUENCY_MAP[value as SnakeFlexiblePaymentFrequency] ?? null;
};

export const findPreferredMonthlyPaymentPlan = (
  paymentPlans: Array<PaymentPlanType> | undefined
): PaymentPlanType | undefined => {
  if (!paymentPlans || paymentPlans.length === 0) return undefined;

  const plansWithLowestDownPayment =
    findPlansWithLowestDownPayment(paymentPlans);

  if (plansWithLowestDownPayment.length === 1) {
    return plansWithLowestDownPayment[0];
  }

  const [bestPaymentPlan] = findPlansWithLowestInstallment(
    plansWithLowestDownPayment
  );

  return bestPaymentPlan || plansWithLowestDownPayment[0];
};

export const findPreferredSemiMonthlyPaymentPlan = (
  paymentPlans: Array<PaymentPlanType> | undefined
): PaymentPlanType | undefined => {
  if (!paymentPlans || paymentPlans.length === 0) return undefined;

  // Currently there is only one semi-monthly plan
  return paymentPlans[0];
};

export const findPreferredInFullPaymentPlan = (
  paymentPlans: Array<PaymentPlanType> | undefined
): PaymentPlanType | undefined => {
  if (!paymentPlans || paymentPlans.length === 0) return undefined;

  // Currently there is only one in-full plan
  return paymentPlans[0];
};

export const getPaymentPlansByType = (
  arr: PaymentPlanType[] | undefined,
  type: PaymentFrequencyEnum
) => {
  if (!arr) return [];
  return arr?.filter(
    (paymentPlan) => paymentPlan.paymentFrequencyIdentifier === type
  );
};

const findPlansWithLowestDownPayment = (
  paymentPlans: PaymentPlanType[]
): PaymentPlanType[] => {
  const lowestDownPaymentTotal = paymentPlans.reduce((lowest, current) => {
    const currentDownpayment = stringToNumber(current.downPaymentAmount);

    return currentDownpayment < lowest ? currentDownpayment : lowest;
  }, Infinity);

  return paymentPlans.filter(
    (plan) => stringToNumber(plan.downPaymentAmount) === lowestDownPaymentTotal
  );
};

const findPlansWithLowestInstallment = (
  paymentPlans: PaymentPlanType[]
): PaymentPlanType[] => {
  const lowestInstallmentTotal = paymentPlans.reduce((lowest, current) => {
    const currentInstallment = stringToNumber(current.installmentAmount);

    return currentInstallment < lowest ? currentInstallment : lowest;
  }, Infinity);

  return paymentPlans.filter(
    (plan) => stringToNumber(plan.installmentAmount) === lowestInstallmentTotal
  );
};

const stringToNumber = (amount: string): number => {
  return parseFloat(amount.replace(/[^0-9.]+/g, ''));
};
